import React, { useState, useEffect, useContext } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import { LayoutDashboard, Package, FileText, MessageSquare, Newspaper, HelpCircle, LogOut, Plus, Eye, Edit, Trash2, ChevronRight, Mail, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const sidebarLinks = [
  { label: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
  { label: 'Products', href: '/admin/products', icon: Package },
  { label: 'Product Enquiries', href: '/admin/enquiries', icon: ClipboardList },
  { label: 'RFQs', href: '/admin/rfqs', icon: MessageSquare },
  { label: 'Downloads', href: '/admin/downloads', icon: FileText },
  { label: 'News', href: '/admin/news', icon: Newspaper },
  { label: 'FAQs', href: '/admin/faqs', icon: HelpCircle },
  { label: 'Contacts', href: '/admin/contacts', icon: Mail },
];

function AdminLayout({ children }) {
  const location = useLocation();
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/admin'); };

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--surface-0)' }}>
      <aside className="admin-sidebar border-r hidden md:flex flex-col" style={{ background: 'var(--surface-1)' }}>
        <div className="p-4 border-b">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-xs" style={{ background: 'var(--brand-teal)' }}>LC</div>
            <span className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Admin Panel</span>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {sidebarLinks.map(link => (
            <Link key={link.href} to={link.href}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                location.pathname === link.href ? 'bg-[var(--surface-2)] text-[var(--brand-teal)] font-medium' : 'text-muted-foreground hover:text-[var(--brand-navy)] hover:bg-[var(--surface-2)]'
              }`}>
              <link.icon className="w-4 h-4" />
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="p-3 border-t">
          <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-red-500 w-full rounded-md hover:bg-[var(--surface-2)] transition-colors">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>
      <main className="flex-1 p-4 sm:p-6 overflow-auto">{children}</main>
    </div>
  );
}

// Dashboard
function Dashboard() {
  const { token } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStats(r.data)).catch(() => {});
  }, [token]);

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Dashboard</h1>
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Products', value: stats.products_count, color: 'var(--brand-teal)' },
            { label: 'New RFQs', value: stats.rfqs_new, color: '#EF4444' },
            { label: 'Total RFQs', value: stats.rfqs_count, color: 'var(--brand-navy)' },
            { label: 'Downloads', value: stats.downloads_count, color: 'var(--brand-slate)' },
            { label: 'News Articles', value: stats.news_count, color: 'var(--brand-teal)' },
            { label: 'FAQs', value: stats.faqs_count, color: 'var(--brand-slate)' },
            { label: 'Contacts', value: stats.contacts_count, color: 'var(--brand-navy)' },
          ].map((s, i) => (
            <Card key={i} style={{ background: 'var(--surface-1)' }}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk', color: s.color }}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// RFQs Management
function RFQsManager() {
  const { token } = useContext(AuthContext);
  const [rfqs, setRfqs] = useState([]);
  const [selectedRfq, setSelectedRfq] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const loadRfqs = () => {
    const params = statusFilter !== 'all' ? { status: statusFilter } : {};
    axios.get(`${API_URL}/api/admin/rfqs`, { headers: { Authorization: `Bearer ${token}` }, params })
      .then(r => setRfqs(r.data.rfqs || [])).catch(() => {});
  };

  useEffect(() => { loadRfqs(); }, [token, statusFilter]);

  const updateStatus = async (rfqId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/rfqs/${rfqId}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Status updated');
      loadRfqs();
    } catch { toast.error('Failed to update'); }
  };

  const statusColors = { new: 'bg-blue-100 text-blue-700', in_review: 'bg-yellow-100 text-yellow-700', quoted: 'bg-green-100 text-green-700', won: 'bg-emerald-100 text-emerald-700', lost: 'bg-red-100 text-red-700' };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>RFQ Management</h1>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="in_review">In Review</SelectItem>
            <SelectItem value="quoted">Quoted</SelectItem>
            <SelectItem value="won">Won</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        {rfqs.map(rfq => (
          <Card key={rfq.rfq_id} style={{ background: 'var(--surface-1)' }}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm" style={{ color: 'var(--brand-navy)' }}>{rfq.company_name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[rfq.status] || 'bg-gray-100 text-gray-700'}`}>{rfq.status?.replace('_', ' ')}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{rfq.contact_name} &bull; {rfq.email} {rfq.model && `\u2022 Model: ${rfq.model}`}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{new Date(rfq.created_at).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={rfq.status} onValueChange={(v) => updateStatus(rfq.rfq_id, v)}>
                    <SelectTrigger className="w-[120px] h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_review">In Review</SelectItem>
                      <SelectItem value="quoted">Quoted</SelectItem>
                      <SelectItem value="won">Won</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                  <Dialog>
                    <DialogTrigger asChild><Button variant="outline" size="sm"><Eye className="w-3.5 h-3.5" /></Button></DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader><DialogTitle>RFQ Details</DialogTitle></DialogHeader>
                      <div className="space-y-3 text-sm">
                        <div className="grid grid-cols-2 gap-2">
                          <div><span className="text-muted-foreground">Company:</span> <strong>{rfq.company_name}</strong></div>
                          <div><span className="text-muted-foreground">Contact:</span> <strong>{rfq.contact_name}</strong></div>
                          <div><span className="text-muted-foreground">Email:</span> {rfq.email}</div>
                          <div><span className="text-muted-foreground">Phone:</span> {rfq.phone || '\u2014'}</div>
                          <div><span className="text-muted-foreground">Model:</span> {rfq.model || '\u2014'}</div>
                          <div><span className="text-muted-foreground">Capacity:</span> {rfq.required_capacity || '\u2014'}</div>
                          <div><span className="text-muted-foreground">Leaving Temp:</span> {rfq.leaving_water_temp || '\u2014'}</div>
                          <div><span className="text-muted-foreground">Entering Temp:</span> {rfq.entering_water_temp || '\u2014'}</div>
                          <div><span className="text-muted-foreground">Electrical:</span> {rfq.electrical_supply || '\u2014'}</div>
                          <div><span className="text-muted-foreground">Location:</span> {rfq.site_location || '\u2014'}</div>
                        </div>
                        {rfq.cooling_tower_data && <div><span className="text-muted-foreground">Cooling Tower:</span> {rfq.cooling_tower_data}</div>}
                        {rfq.additional_notes && <div><span className="text-muted-foreground">Notes:</span> {rfq.additional_notes}</div>}
                        {rfq.file_name && <div><span className="text-muted-foreground">Attachment:</span> {rfq.file_name}</div>}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {rfqs.length === 0 && <p className="text-center text-muted-foreground py-8">No RFQs found</p>}
      </div>
    </div>
  );
}

// Products Manager
function ProductsManager() {
  const { token } = useContext(AuthContext);
  const [products, setProducts] = useState([]);

  const loadProducts = () => {
    axios.get(`${API_URL}/api/products?limit=50`)
      .then(r => setProducts(r.data.products || [])).catch(() => {});
  };
  useEffect(() => { loadProducts(); }, []);

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Product deleted');
      loadProducts();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Products</h1>
        <Badge variant="secondary">{products.length} products</Badge>
      </div>
      <div className="space-y-2" data-testid="admin-products-table">
        {products.map(p => (
          <Card key={p._id} style={{ background: 'var(--surface-1)' }}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm" style={{ color: 'var(--brand-navy)' }}>{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.category?.replace(/-/g, ' ')} &bull; {p.specs?.nominal_capacity_kw} kW &bull; {p.specs?.refrigerant}</p>
              </div>
              <div className="flex gap-2">
                <Link to={`/products/detail/${p.slug}`}><Button variant="outline" size="sm"><Eye className="w-3.5 h-3.5" /></Button></Link>
                <Button variant="outline" size="sm" onClick={() => deleteProduct(p._id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Downloads Manager
function DownloadsManager() {
  const { token } = useContext(AuthContext);
  const [downloads, setDownloads] = useState([]);

  const loadDownloads = () => {
    axios.get(`${API_URL}/api/downloads?limit=50`)
      .then(r => setDownloads(r.data.downloads || [])).catch(() => {});
  };
  useEffect(() => { loadDownloads(); }, []);

  const deleteDownload = async (id) => {
    if (!window.confirm('Delete this download?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/downloads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      loadDownloads();
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Downloads</h1>
        <Badge variant="secondary">{downloads.length} files</Badge>
      </div>
      <div className="space-y-2">
        {downloads.map(dl => (
          <Card key={dl._id} style={{ background: 'var(--surface-1)' }}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm" style={{ color: 'var(--brand-navy)' }}>{dl.title}</p>
                <p className="text-xs text-muted-foreground">{dl.doc_type} &bull; v{dl.version} &bull; {dl.file_name}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => deleteDownload(dl._id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// News Manager
function NewsManager() {
  const { token } = useContext(AuthContext);
  const [articles, setArticles] = useState([]);
  const loadNews = () => {
    axios.get(`${API_URL}/api/news?limit=50`)
      .then(r => setArticles(r.data.articles || [])).catch(() => {});
  };
  useEffect(() => { loadNews(); }, []);

  const deleteArticle = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/news/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      loadNews();
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>News & Case Studies</h1>
        <Badge variant="secondary">{articles.length} articles</Badge>
      </div>
      <div className="space-y-2">
        {articles.map(a => (
          <Card key={a._id} style={{ background: 'var(--surface-1)' }}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm" style={{ color: 'var(--brand-navy)' }}>{a.title}</p>
                <p className="text-xs text-muted-foreground">{a.category} &bull; {new Date(a.published_at).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-2">
                <Link to={`/news/${a._id}`}><Button variant="outline" size="sm"><Eye className="w-3.5 h-3.5" /></Button></Link>
                <Button variant="outline" size="sm" onClick={() => deleteArticle(a._id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// FAQs Manager
function FAQsManager() {
  const { token } = useContext(AuthContext);
  const [faqs, setFaqs] = useState([]);
  const loadFaqs = () => {
    axios.get(`${API_URL}/api/faqs`).then(r => setFaqs(r.data || [])).catch(() => {});
  };
  useEffect(() => { loadFaqs(); }, []);

  const deleteFaq = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/faqs/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted');
      loadFaqs();
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>FAQs</h1>
        <Badge variant="secondary">{faqs.length} items</Badge>
      </div>
      <div className="space-y-2">
        {faqs.map(faq => (
          <Card key={faq._id} style={{ background: 'var(--surface-1)' }}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm" style={{ color: 'var(--brand-navy)' }}>{faq.question}</p>
                <p className="text-xs text-muted-foreground">{faq.category} &bull; Order: {faq.order}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => deleteFaq(faq._id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Contacts Manager
function ContactsManager() {
  const { token } = useContext(AuthContext);
  const [contacts, setContacts] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setContacts(r.data.contacts || [])).catch(() => {});
  }, [token]);

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Contact Submissions</h1>
      <div className="space-y-2">
        {contacts.map(c => (
          <Card key={c._id} style={{ background: 'var(--surface-1)' }}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-sm" style={{ color: 'var(--brand-navy)' }}>{c.name} {c.company && `(${c.company})`}</p>
                  <p className="text-xs text-muted-foreground">{c.email} {c.phone && `\u2022 ${c.phone}`}</p>
                  {c.subject && <p className="text-xs text-muted-foreground mt-1">Subject: {c.subject}</p>}
                  <p className="text-xs text-muted-foreground mt-1">{c.message}</p>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(c.created_at).toLocaleDateString()}</span>
              </div>
            </CardContent>
          </Card>
        ))}
        {contacts.length === 0 && <p className="text-center text-muted-foreground py-8">No contact submissions yet</p>}
      </div>
    </div>
  );
}

// Product Enquiries Manager
function ProductEnquiriesManager() {
  const { token } = useContext(AuthContext);
  const [enquiries, setEnquiries] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const loadEnquiries = () => {
    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    if (categoryFilter !== 'all') params.category = categoryFilter;
    axios.get(`${API_URL}/api/admin/product-enquiries`, { headers: { Authorization: `Bearer ${token}` }, params })
      .then(r => setEnquiries(r.data.enquiries || [])).catch(() => {});
  };

  useEffect(() => { loadEnquiries(); }, [token, statusFilter, categoryFilter]);

  const updateStatus = async (enquiryId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/product-enquiries/${enquiryId}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Status updated');
      loadEnquiries();
    } catch { toast.error('Failed to update'); }
  };

  const statusColors = { new: 'bg-blue-100 text-blue-700', contacted: 'bg-yellow-100 text-yellow-700', quoted: 'bg-green-100 text-green-700', closed: 'bg-gray-100 text-gray-700' };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Product Enquiries</h1>
        <div className="flex gap-2">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="Water Cooled Brine Chillers">Brine Chillers</SelectItem>
              <SelectItem value="Water Cooled Chillers (Chilled Water)">Chilled Water</SelectItem>
              <SelectItem value="Ammonia Chillers">Ammonia</SelectItem>
              <SelectItem value="Ultra Low Temperature Chillers">Ultra Low Temp</SelectItem>
              <SelectItem value="Custom Tailor-Made Chillers">Custom</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="quoted">Quoted</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        {enquiries.map(enq => (
          <Card key={enq.enquiry_id} style={{ background: 'var(--surface-1)' }}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-xs font-semibold" style={{ color: 'var(--brand-teal)' }}>{enq.enquiry_id}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[enq.status] || 'bg-gray-100 text-gray-700'}`}>{enq.status}</span>
                    <Badge variant="secondary" className="text-[10px]">{enq.product_category}</Badge>
                  </div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>{enq.company_name} — {enq.contact_name}</p>
                  <p className="text-xs text-muted-foreground">{enq.email} {enq.phone && `• ${enq.phone}`}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                    {enq.required_capacity && <span>Capacity: {enq.required_capacity}</span>}
                    {enq.outlet_temperature && <span>Outlet: {enq.outlet_temperature}</span>}
                    {enq.condenser_type && <span>Condenser: {enq.condenser_type}</span>}
                    {enq.evaporator_tube_material && <span>Tubes: {enq.evaporator_tube_material}</span>}
                    {enq.compressor_brand && <span>Compressor: {enq.compressor_brand}</span>}
                    {enq.installation_location && <span>Location: {enq.installation_location}</span>}
                    {enq.iot_bms_required === 'yes' && <span className="font-medium" style={{ color: 'var(--brand-teal)' }}>IoT/BMS: Yes</span>}
                  </div>
                  {enq.special_requirements && <p className="text-xs text-muted-foreground mt-1 italic">"{enq.special_requirements}"</p>}
                  <p className="text-[10px] text-muted-foreground mt-1">{new Date(enq.created_at).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Select value={enq.status} onValueChange={(v) => updateStatus(enq.enquiry_id, v)}>
                    <SelectTrigger className="w-[120px] h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="quoted">Quoted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {enquiries.length === 0 && <p className="text-center text-muted-foreground py-8">No product enquiries found</p>}
      </div>
    </div>
  );
}

// Main Admin Dashboard wrapper
export default function AdminDashboard() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) navigate('/admin');
  }, [token, navigate]);

  if (!token) return null;

  return (
    <AdminLayout>
      <Routes>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="products" element={<ProductsManager />} />
        <Route path="enquiries" element={<ProductEnquiriesManager />} />
        <Route path="rfqs" element={<RFQsManager />} />
        <Route path="downloads" element={<DownloadsManager />} />
        <Route path="news" element={<NewsManager />} />
        <Route path="faqs" element={<FAQsManager />} />
        <Route path="contacts" element={<ContactsManager />} />
      </Routes>
    </AdminLayout>
  );
}
